import React, {useCallback, useEffect, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import createAxiosInstance from '../support/AxiosSupport';
import {formatDateTime} from '../utils/dateTime';
import {pluralItems} from '../utils/plural';
import type {DisposalIntent, ItemLifecycleStatus} from '../support/itemLifecycle';
import type {VisualProfileData} from './ItemCompatibilityAttributes';
import AuthImage from './AuthImage';
import Spinner from './Spinner';

/** Машиночитаемый код совета лестницы решений (backend ItemAdviceCode). */
type AdviceCode = 'KEEP' | 'MOVE_TO_HOME' | 'MOVE_TO_WORK' | 'DISPOSE' | 'GIVE_AWAY' | 'DONATE';

interface AiProfile {
    itemId: string;
    shortSummary: string | null;
    visualSummary: string | null;
    functionalSummary: string | null;
    duplicateSearchText: string | null;
    outfitMatchingText: string | null;
    styleReasoningText: string | null;
    itemAdvice: string | null;
    /** Код совета для кнопки-действия (ai-advice-ladder); null — старый профиль или мусор от модели. */
    adviceCode: AdviceCode | null;

    // --- Согласованное решение по вещи (item-decision-consistency, F02) ---

    /** Действие из единой проекции решения — то же, что в ленте и в разборе. */
    decisionAction?: AdviceCode | null;
    /** Подпись действия; «Проверить», когда сигналы разошлись. */
    decisionActionLabel?: string | null;
    /** 2–3 причины человеческим языком. */
    decisionReasons?: string[];
    /** Откуда взято решение: DECLUTTER_VERDICT | AI_ADVICE. */
    decisionSource?: string | null;
    /** Сигналы разошлись — уверенного выбытия карточка не предлагает. */
    decisionUncertain?: boolean;
    /** «Недостатки и износ» — честное перечисление видимых недостатков (strict-recognition). */
    conditionFlaws: string | null;
    modelVersion: string | null;
    status: 'PENDING' | 'GENERATING' | 'COMPLETED' | 'FAILED' | null;
    createdAt: string | null;
    updatedAt: string | null;
    aiRatingOverall: number | null;
    occasionRatingDaily: number | null;
    occasionRatingOffice: number | null;
    occasionRatingSport: number | null;
    occasionRatingEvening: number | null;
    occasionRatingOutdoor: number | null;
}

interface DuplicateCandidate {
    itemId: string;
    duplicateType: 'same_item' | 'visual_duplicate' | 'functional_duplicate';
    score: number;
    reason: string;
}

interface DuplicateSearchResponse {
    itemId: string;
    candidates: DuplicateCandidate[];
    /** `NOT_INDEXED` — искать не по чему: гардероб ещё не проиндексирован (Q18). */
    status?: 'OK' | 'NOT_INDEXED' | null;
    /** Сколько активных вещей без векторного индекса. */
    unindexedCount?: number | null;
}

interface ItemPreview {
    name: string | null;
    categoryName: string | null;
    brandName: string | null;
    photoUrls: string[] | null;
}

const STATUS_LABEL: Record<string, string> = {
    PENDING: 'Ожидает',
    GENERATING: 'Генерируется',
    COMPLETED: 'Готово',
    FAILED: 'Ошибка',
};

const STATUS_CLS: Record<string, string> = {
    PENDING: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
    GENERATING: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
    COMPLETED: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
    FAILED: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
};

const DUPE_TYPE_LABEL: Record<string, string> = {
    same_item: 'Та же вещь',
    visual_duplicate: 'Визуальный дубль',
    functional_duplicate: 'Функциональный дубль',
};

const DUPE_TYPE_CLS: Record<string, string> = {
    same_item: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
    visual_duplicate: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
    functional_duplicate: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
};

/** Действие по коду совета — переиспользует механизмы item-lifecycle-menu (dispose обратим через корзину). */
interface AdviceAction {
    label: string;
    kind: 'lifecycle' | 'dispose';
    payload: ItemLifecycleStatus | DisposalIntent;
}

const ADVICE_ACTIONS: Partial<Record<AdviceCode, AdviceAction>> = {
    MOVE_TO_HOME: {label: 'Перевести в домашние', kind: 'lifecycle', payload: 'MOVED_TO_HOME'},
    MOVE_TO_WORK: {label: 'Перевести в рабочие', kind: 'lifecycle', payload: 'MOVED_TO_WORK'},
    DISPOSE: {label: 'Выбросить', kind: 'dispose', payload: 'DISPOSED'},
    GIVE_AWAY: {label: 'Отдать', kind: 'dispose', payload: 'GIVEN_AWAY'},
    DONATE: {label: 'Пожертвовать', kind: 'dispose', payload: 'DONATED'},
};

/** Формат raw-значения оси вектора: три знака, как хранит backend (NUMERIC(4,3)). */
const fmtScore = (value: number): string => value.toFixed(3);

/** Строка OKLCH записи палитры для «Технических данных»; без координат — прочерк. */
const fmtOklch = (entry: {oklch?: {l: number | null; c: number | null; h: number | null} | null}): string => {
    const o = entry.oklch;
    if (!o || o.l == null || o.c == null) return '—';
    return `oklch(${o.l} ${o.c} ${o.h ?? 0})`;
};

/**
 * Секция «Скрытые параметры (вектор сочетаемости)» в аккордеоне «Технические данные»
 * (ai-visual-profile-sync, FR-3): raw-значения осей, style affinity, палитра с долями
 * и OKLCH, метаданные источника. Read-only; пустой профиль — подсказка про AI-анализ.
 */
const HiddenParamsSection: React.FC<{
    profile: VisualProfileData | null;
    formatDt: (s: string | null | undefined) => string | null;
}> = ({profile, formatDt}) => {
    const scales: Array<{ label: string; value: number | null | undefined }> = profile
        ? [
            {label: 'formality', value: profile.formalityScore},
            {label: 'volume', value: profile.volumeScore},
            {label: 'structure', value: profile.structureScore},
            {label: 'length', value: profile.lengthScore},
            {label: 'waistDefinition', value: profile.waistDefinitionScore},
            {label: 'patternIntensity', value: profile.patternIntensity},
            {label: 'patternScale', value: profile.patternScaleScore},
            {label: 'patternContrast', value: profile.patternContrastScore},
            {label: 'patternCoverage', value: profile.patternCoverageScore},
            {label: 'detail', value: profile.detailLevel},
            {label: 'ornament', value: profile.ornamentLevel},
            {label: 'hardware', value: profile.hardwareLevel},
            {label: 'logo', value: profile.logoIntensity},
            {label: 'distressing', value: profile.distressingLevel},
        ]
        : [];
    const visibleScales = scales.filter((s): s is { label: string; value: number } => s.value != null);
    const affinity = profile?.styleAffinity
        ? Object.entries(profile.styleAffinity).sort((a, b) => b[1] - a[1])
        : [];
    // Material-вектор компактной строкой (attribute-vector-v2, FR-5): 8+2 осей в raw-формате.
    const materialEntries = profile?.material
        ? Object.entries(profile.material).filter((e): e is [string, number] => e[1] != null)
        : [];

    return (
        <div data-testid="hidden-params" className="pt-2 border-t border-gray-100 dark:border-gray-700/50 space-y-2">
            <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                Скрытые параметры (вектор сочетаемости)
            </p>
            {!profile && (
                <p data-testid="hidden-params-empty" className="text-xs text-gray-500 dark:text-gray-400">
                    Вектор не рассчитан. Он заполнится при следующем AI-анализе вещи.
                </p>
            )}
            {profile && (
                <div className="space-y-2 text-xs text-gray-600 dark:text-gray-300">
                    {visibleScales.length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
                            {visibleScales.map(s => (
                                <span key={s.label} data-testid={`hidden-param-${s.label}`}>
                                    {s.label}: <span className="font-mono">{fmtScore(s.value)}</span>
                                </span>
                            ))}
                        </div>
                    )}
                    {materialEntries.length > 0 && (
                        <div data-testid="hidden-params-material" className="flex flex-wrap gap-x-3 gap-y-1">
                            <span className="text-gray-400 dark:text-gray-500">material:</span>
                            {materialEntries.map(([axis, value]) => (
                                <span key={axis} className="font-mono">{axis}: {fmtScore(value)}</span>
                            ))}
                        </div>
                    )}
                    {affinity.length > 0 && (
                        <div data-testid="hidden-params-affinity" className="flex flex-wrap gap-x-3 gap-y-1">
                            <span className="text-gray-400 dark:text-gray-500">style affinity:</span>
                            {affinity.map(([code, weight]) => (
                                <span key={code} className="font-mono">{code}: {fmtScore(weight)}</span>
                            ))}
                        </div>
                    )}
                    {!!profile.palette?.length && (
                        <div data-testid="hidden-params-palette" className="space-y-0.5">
                            <span className="text-gray-400 dark:text-gray-500">палитра:</span>
                            {profile.palette.map((entry, idx) => (
                                <div key={`${entry.refColorId ?? 'inline'}-${idx}`} className="font-mono">
                                    {Math.round(entry.weight * 100)}% · {fmtOklch(entry)}
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-2 text-gray-400 dark:text-gray-500 pt-1">
                        {profile.source && <span>Источник: {profile.source}</span>}
                        {profile.confidence != null && (
                            <span>Уверенность: <span className="font-mono">{fmtScore(profile.confidence)}</span></span>
                        )}
                        {profile.styleSchemaVersion && <span>Схема стилей: {profile.styleSchemaVersion}</span>}
                        {profile.modelVersion && <span>Модель: {profile.modelVersion}</span>}
                        {profile.updatedAt && <span>Обновлён: {formatDt(profile.updatedAt)}</span>}
                    </div>
                </div>
            )}
        </div>
    );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({title, children}) => (
    <div className="space-y-1">
        <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">{title}</p>
        <div className="text-sm text-gray-900 dark:text-gray-100">{children}</div>
    </div>
);


interface Props {
    itemId: string | undefined;
    conditionName?: string | null;
    defectNames?: string | null;
    /** Текущий статус вещи — скрывает CTA, если вещь уже в целевом состоянии. */
    lifecycleStatus?: ItemLifecycleStatus | null;
    thrownAway?: boolean | null;
    inTrashBin?: boolean | null;
    /** Права гардероба (EDIT_ITEMS / MOVE_TO_TRASH) — без права CTA скрыта. */
    canEdit?: boolean;
    canMoveToTrash?: boolean;
    /** Перезагрузка вещи после успешного действия по совету. */
    onItemChanged?: () => void;
    /** Скрытые параметры вещи из детальной карточки (ai-visual-profile-sync, FR-3). */
    visualProfile?: VisualProfileData | null;
}

const ClothingAiProfileTab: React.FC<Props> = ({
    itemId, conditionName, defectNames,
    lifecycleStatus, thrownAway, inTrashBin, canEdit, canMoveToTrash, onItemChanged,
    visualProfile,
}) => {
    const navigate = useNavigate();
    // Вещь в корзине или выбывшая: только чтение профиля (retired-item-actions-guard).
    const isFrozen = !!inTrashBin || !!thrownAway;

    const [profile, setProfile] = useState<AiProfile | null>(null);
    const [loadingProfile, setLoadingProfile] = useState(false);
    const [profileError, setProfileError] = useState<string | null>(null);
    const [regenerating, setRegenerating] = useState(false);

    const [duplicates, setDuplicates] = useState<DuplicateSearchResponse | null>(null);
    const [candidateDetails, setCandidateDetails] = useState<Record<string, ItemPreview>>({});
    const [searchingDupes, setSearchingDupes] = useState(false);
    const [dupesError, setDupesError] = useState<string | null>(null);

    const [feedbackSent, setFeedbackSent] = useState<Set<string>>(new Set());
    const [techExpanded, setTechExpanded] = useState(false);

    const [adviceActionBusy, setAdviceActionBusy] = useState(false);
    const [adviceActionError, setAdviceActionError] = useState<string | null>(null);

    const fetchProfile = useCallback(async () => {
        if (!itemId) return;
        setLoadingProfile(true);
        setProfileError(null);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<AiProfile>(`/clothing/${itemId}/ai-profile`);
            setProfile(res.data);
        } catch (err: any) {
            if (err?.response?.status === 404) {
                setProfile(null);
                setProfileError(null);
            } else {
                setProfileError('Не удалось загрузить AI-профиль');
            }
        } finally {
            setLoadingProfile(false);
        }
    }, [itemId]);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    // Автообновление статуса пока идёт генерация
    useEffect(() => {
        if (profile?.status !== 'GENERATING') return;
        const timer = setInterval(fetchProfile, 4000);
        return () => clearInterval(timer);
    }, [profile?.status, fetchProfile]);

    const handleRegenerate = async () => {
        if (!itemId) return;
        setRegenerating(true);
        try {
            const api = await createAxiosInstance();
            await api.post(`/clothing/${itemId}/ai-profile/regenerate`);
            await fetchProfile();
        } catch {
            // silent
        } finally {
            setRegenerating(false);
        }
    };

    const handleSearchDupes = async () => {
        if (!itemId) return;
        setSearchingDupes(true);
        setDupesError(null);
        setDuplicates(null);
        setCandidateDetails({});
        try {
            const api = await createAxiosInstance();
            const res = await api.post<DuplicateSearchResponse>(`/clothing/${itemId}/duplicates/search`);
            setDuplicates(res.data);
            if (res.data.candidates.length > 0) {
                const details = await Promise.all(
                    res.data.candidates.map(c =>
                        api.get<ItemPreview>(`/clothing/items/${c.itemId}`)
                            .then(r => ({id: c.itemId, data: r.data}))
                            .catch(() => ({id: c.itemId, data: {name: null, categoryName: null, brandName: null, photoUrls: null}}))
                    )
                );
                setCandidateDetails(Object.fromEntries(details.map(d => [d.id, d.data])));
            }
        } catch {
            setDupesError('Не удалось выполнить поиск дублей');
        } finally {
            setSearchingDupes(false);
        }
    };

    const handleFeedback = async (candidateId: string, decision: 'confirmed' | 'rejected') => {
        if (!itemId) return;
        const key = `${candidateId}_${decision}`;
        try {
            const api = await createAxiosInstance();
            await api.post(`/clothing/${itemId}/similarity-feedback`, {
                itemAId: itemId,
                itemBId: candidateId,
                relationType: 'visual_duplicate',
                decision,
            });
            setFeedbackSent(prev => new Set(prev).add(candidateId));
            if (decision === 'rejected') {
                setDuplicates(prev =>
                    prev ? {
                        ...prev,
                        candidates: prev.candidates.filter(c => c.itemId !== candidateId),
                    } : prev
                );
            }
        } catch {
            // silent
        }
    };

    /**
     * CTA по коду совета (ai-advice-ladder): null — кнопки нет. Скрыта для выбывшей вещи,
     * без права, если вещь уже в целевом статусе или уже в корзине (намерение фиксирует корзина).
     * UI-скрытие не заменяет серверный enforcement (lifecycle/dispose проверяют права сами).
     */
    const resolveAdviceAction = (code: AdviceCode | null | undefined): AdviceAction | null => {
        if (!code || code === 'KEEP' || thrownAway) return null;
        const action = ADVICE_ACTIONS[code];
        if (!action) return null;
        if (action.kind === 'lifecycle') {
            if (!canEdit || lifecycleStatus === action.payload) return null;
            return action;
        }
        if (!canMoveToTrash || inTrashBin) return null;
        return action;
    };

    const handleAdviceAction = async (action: AdviceAction) => {
        if (!itemId) return;
        setAdviceActionBusy(true);
        setAdviceActionError(null);
        try {
            const api = await createAxiosInstance();
            if (action.kind === 'lifecycle') {
                await api.post(`/clothing/items/${itemId}/lifecycle`, {status: action.payload});
            } else {
                await api.post(`/clothing/items/${itemId}/dispose`, {intent: action.payload});
            }
            onItemChanged?.();
        } catch {
            setAdviceActionError('Не удалось выполнить действие');
        } finally {
            setAdviceActionBusy(false);
        }
    };

    const formatDt = (s: string | null | undefined) => (s ? formatDateTime(s) : null);

    if (loadingProfile) {
        return (
            <div className="flex justify-center py-10">
                <Spinner/>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* AI Profile block */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">AI-профиль</h3>
                        {profile?.status && (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CLS[profile.status] ?? STATUS_CLS.PENDING}`}>
                                {profile.status === 'GENERATING' && (
                                    <span className="inline-block w-2 h-2 mr-1 rounded-full bg-blue-500 animate-pulse"/>
                                )}
                                {STATUS_LABEL[profile.status] ?? profile.status}
                            </span>
                        )}
                    </div>
                    {/* Вещь вне активного гардероба на анализ не ставится: бэкенд ответит 409
                        (retired-item-actions-guard), сохранённый профиль остаётся читаемым. */}
                    {!isFrozen && (
                        <button
                            onClick={handleRegenerate}
                            disabled={regenerating || profile?.status === 'GENERATING'}
                            className="btn-secondary py-1 px-3 text-sm disabled:opacity-50"
                        >
                            {regenerating ? 'Запускаем...' : 'Обновить профиль'}
                        </button>
                    )}
                </div>

                {(conditionName || defectNames) && (
                    <div className="flex flex-wrap gap-2 text-sm">
                        {conditionName && (
                            <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                {conditionName}
                            </span>
                        )}
                        {defectNames && (
                            <span className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                                Дефекты: {defectNames}
                            </span>
                        )}
                    </div>
                )}

                {profileError && (
                    <p className="text-sm text-red-600 dark:text-red-400">{profileError}</p>
                )}

                {!profile && !profileError && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {isFrozen
                            ? 'AI-профиль не создавался. Вещь вне активного гардероба — анализ для неё не запускается.'
                            : 'AI-профиль ещё не создан. Нажмите «Обновить профиль» для запуска.'}
                    </p>
                )}

                {profile?.status === 'COMPLETED' && (
                    <div className="space-y-3">
                        {profile.shortSummary && (
                            <Section title="Краткое описание">{profile.shortSummary}</Section>
                        )}

                        {profile.visualSummary && (
                            <Section title="Визуальное описание">{profile.visualSummary}</Section>
                        )}
                        {profile.functionalSummary && (
                            <Section title="Функциональность">{profile.functionalSummary}</Section>
                        )}
                        {profile.outfitMatchingText && (
                            <Section title="Сочетаемость">{profile.outfitMatchingText}</Section>
                        )}
                        {profile.styleReasoningText && (
                            <Section title="Стилевые заметки">{profile.styleReasoningText}</Section>
                        )}
                        {profile.conditionFlaws && (
                            <Section title="Недостатки и износ">
                                <span
                                    className="text-amber-700 dark:text-amber-300"
                                    data-testid="condition-flaws"
                                >
                                    {profile.conditionFlaws}
                                </span>
                            </Section>
                        )}
                        {(profile.itemAdvice || profile.decisionAction) && (
                            <Section title={profile.decisionAction ? 'Решение по вещи' : 'Совет'}>
                                {(() => {
                                    // F02: карточка читает ту же проекцию решения, что лента и разбор;
                                    // текст модели остаётся пояснением, а не вторым мнением.
                                    const adviceAction = resolveAdviceAction(
                                        profile.decisionAction ?? profile.adviceCode);
                                    return (
                                        <div className="space-y-2">
                                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                                <span className="flex-1 min-w-[12rem]">
                                                    {profile.decisionActionLabel ? (
                                                        <span data-testid="item-decision">
                                                            <span className="font-medium">
                                                                {profile.decisionActionLabel}
                                                            </span>
                                                            {(profile.decisionReasons ?? []).length > 0 && (
                                                                <ul className="mt-1 list-disc list-inside text-sm text-gray-500 dark:text-gray-400">
                                                                    {(profile.decisionReasons ?? []).map(r => (
                                                                        <li key={r}>{r}</li>
                                                                    ))}
                                                                </ul>
                                                            )}
                                                        </span>
                                                    ) : profile.itemAdvice}
                                                </span>
                                                {adviceAction && (
                                                    <button
                                                        data-testid="advice-cta"
                                                        onClick={() => handleAdviceAction(adviceAction)}
                                                        disabled={adviceActionBusy}
                                                        className="btn-secondary py-1 px-3 text-sm disabled:opacity-50 flex-shrink-0"
                                                    >
                                                        {adviceActionBusy ? 'Выполняем…' : adviceAction.label}
                                                    </button>
                                                )}
                                            </div>
                                            {profile.decisionUncertain && (
                                                <p className="text-sm text-amber-600 dark:text-amber-400"
                                                   data-testid="item-decision-uncertain">
                                                    Сигналы по вещи расходятся — уверенного совета сейчас нет.
                                                </p>
                                            )}
                                            {profile.decisionActionLabel && profile.itemAdvice && (
                                                <p className="text-sm text-gray-500 dark:text-gray-400"
                                                   data-testid="item-advice-comment">
                                                    Комментарий AI: {profile.itemAdvice}
                                                </p>
                                            )}
                                            {adviceActionError && (
                                                <p className="text-sm text-red-600 dark:text-red-400">{adviceActionError}</p>
                                            )}
                                        </div>
                                    );
                                })()}
                            </Section>
                        )}
                        <div className="flex gap-4 text-xs text-gray-400 dark:text-gray-500 pt-2 border-t border-gray-100 dark:border-gray-700/50">
                            {profile.modelVersion && <span>Модель: {profile.modelVersion}</span>}
                            {profile.updatedAt && <span>Обновлено: {formatDt(profile.updatedAt)}</span>}
                        </div>
                    </div>
                )}

                {profile?.status === 'FAILED' && (
                    <p className="text-sm text-red-500 dark:text-red-400">
                        Генерация завершилась с ошибкой. Попробуйте запустить повторно.
                    </p>
                )}

                {profile?.status === 'GENERATING' && (
                    <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
                        <Spinner/>
                        <span>Генерация в процессе, страница обновляется автоматически…</span>
                    </div>
                )}

                {(profile?.status === 'PENDING' || (!profile && !profileError)) && profile && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        AI-профиль поставлен в очередь и скоро будет создан.
                    </p>
                )}
            </div>

            {/* Duplicate search block */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">Поиск дублей</h3>
                    <button
                        onClick={handleSearchDupes}
                        disabled={searchingDupes || profile?.status !== 'COMPLETED'}
                        className="btn-secondary py-1 px-3 text-sm disabled:opacity-50"
                        title={profile?.status !== 'COMPLETED' ? 'Сначала сгенерируйте AI-профиль' : undefined}
                    >
                        {searchingDupes ? 'Ищем…' : 'Найти дубли'}
                    </button>
                </div>

                {dupesError && (
                    <p className="text-sm text-red-600 dark:text-red-400">{dupesError}</p>
                )}

                {duplicates && duplicates.candidates.length === 0 && (
                    /* Q18: «похожих не найдено» на непостроенном индексе — неправда. */
                    duplicates.status === 'NOT_INDEXED' || (duplicates.unindexedCount ?? 0) > 0 ? (
                        <div className="space-y-2" data-testid="dupes-not-indexed">
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                Индекс не построен
                                {duplicates.unindexedCount != null
                                    ? `: ${pluralItems(duplicates.unindexedCount)} без индекса.`
                                    : '.'}
                                {' '}Пока он не готов, похожие вещи найти не по чему.
                            </p>
                            <Link to="/indexing" data-testid="dupes-index-cta"
                                  className="inline-flex items-center min-h-[44px] px-4 rounded-xl bg-brand-600 text-white text-sm font-medium hover:bg-brand-700">
                                Проиндексировать гардероб
                            </Link>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">Похожих вещей не найдено.</p>
                    )
                )}

                {duplicates && duplicates.candidates.length > 0 && (
                    <div className="space-y-2">
                        {duplicates.candidates.map(c => {
                            const detail = candidateDetails[c.itemId];
                            return (
                                <div
                                    key={c.itemId}
                                    className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800"
                                >
                                    {/* Миниатюра + ссылка */}
                                    <button
                                        onClick={() => navigate(`/clothing/${c.itemId}`)}
                                        className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-700 hover:opacity-80 transition-opacity"
                                    >
                                        {detail?.photoUrls?.[0] ? (
                                            <AuthImage
                                                imageKey={detail.photoUrls[0]}
                                                alt={detail.name ?? ''}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500 text-xs">
                                                ?
                                            </div>
                                        )}
                                    </button>

                                    {/* Инфо о вещи */}
                                    <div className="flex-1 min-w-0 space-y-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${DUPE_TYPE_CLS[c.duplicateType]}`}>
                                                {DUPE_TYPE_LABEL[c.duplicateType] ?? c.duplicateType}
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                сходство {Math.round(c.score * 100)}%
                                            </span>
                                        </div>
                                        {detail && (
                                            <button
                                                onClick={() => navigate(`/clothing/${c.itemId}`)}
                                                className="block text-left hover:underline"
                                            >
                                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                                    {detail.name ?? '—'}
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                    {[detail.brandName, detail.categoryName].filter(Boolean).join(' · ')}
                                                </p>
                                            </button>
                                        )}
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{c.reason}</p>
                                    </div>

                                    {/* Фидбэк */}
                                    {!feedbackSent.has(c.itemId) ? (
                                        <div className="flex gap-1 flex-shrink-0">
                                            <button
                                                onClick={() => handleFeedback(c.itemId, 'confirmed')}
                                                className="px-2 py-1 rounded text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/60"
                                            >
                                                Да
                                            </button>
                                            <button
                                                onClick={() => handleFeedback(c.itemId, 'rejected')}
                                                className="px-2 py-1 rounded text-xs bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/60"
                                            >
                                                Нет
                                            </button>
                                        </div>
                                    ) : (
                                        <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">Оценено</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {!duplicates && !dupesError && (
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                        Поиск дублей использует AI-эмбеддинги для поиска похожих вещей в вашем гардеробе.
                    </p>
                )}
            </div>

            {/* Technical info accordion; виден и когда есть только вектор без AI-профиля (FR-3) */}
            {(profile || visualProfile) && (
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <button
                        type="button"
                        onClick={() => setTechExpanded(v => !v)}
                        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                    >
                        <span>Технические данные</span>
                        <svg
                            className={`w-4 h-4 transition-transform ${techExpanded ? 'rotate-180' : ''}`}
                            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/>
                        </svg>
                    </button>
                    {techExpanded && (
                        <div className="px-4 pb-4 space-y-2 border-t border-gray-200 dark:border-gray-700 pt-3">
                            {profile?.duplicateSearchText && (
                                <Section title="Embedding input">{profile.duplicateSearchText}</Section>
                            )}
                            {profile && (
                                <div className="grid grid-cols-2 gap-2 text-xs text-gray-400 dark:text-gray-500 pt-2 border-t border-gray-100 dark:border-gray-700/50">
                                    <span>Статус: {STATUS_LABEL[profile.status ?? ''] ?? profile.status}</span>
                                    {profile.modelVersion && <span>Модель: {profile.modelVersion}</span>}
                                    {profile.createdAt && <span>Создан: {formatDt(profile.createdAt)}</span>}
                                    {profile.updatedAt && <span>Обновлён: {formatDt(profile.updatedAt)}</span>}
                                </div>
                            )}
                            <HiddenParamsSection profile={visualProfile ?? null} formatDt={formatDt}/>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ClothingAiProfileTab;
